import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventMessages({ eventId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, [eventId]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/events/${eventId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei messaggi');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/messages/${messageId}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setMessages(messages.map(msg => 
        msg.id === messageId ? { ...msg, is_read: true } : msg
      ));
      
      toast.success('Messaggio segnato come letto');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Caricamento messaggi...</div>;
  }

  if (messages.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600 text-lg">Nessun messaggio ricevuto per questo evento</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          {messages.length} {messages.length === 1 ? 'messaggio' : 'messaggi'}
        </h3>
        <span className="text-sm text-gray-600">
          {messages.filter(m => !m.is_read).length} non letti
        </span>
      </div>

      {messages.map((message) => (
        <Card key={message.id} className={message.is_read ? 'bg-white' : 'bg-blue-50 border-blue-200'}>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg">{message.sender_name}</CardTitle>
                <p className="text-sm text-gray-600">{message.sender_email}</p>
              </div>
              <div className="flex items-center gap-2">
                {!message.is_read && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => markAsRead(message.id)}
                    className="gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Segna come letto
                  </Button>
                )}
                <span className="text-xs text-gray-500">
                  {format(new Date(message.created_at), 'dd MMM yyyy, HH:mm', { locale: it })}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-gray-800 whitespace-pre-wrap">{message.message}</p>
            
            <div className="mt-4 pt-4 border-t flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.location.href = `mailto:${message.sender_email}`}
              >
                📧 Rispondi via Email
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
